import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};

// ═══════════════════════════════════════════
// THEME PALETTES
// ═══════════════════════════════════════════
const themes = {
  birthday: {
    id: "birthday",
    label: "Birthday",
    primary: "#f97316",
    primaryDark: "#ea580c",
    primaryLight: "#fed7aa",
    primaryBg: "#fffbf5",
    primaryBgAccent: "#fff7ed",
    pageBg: "#ffffff",
    accent: "#f59e0b",
    headerGradient: "linear-gradient(135deg, #431407 0%, #9a3412 50%, #7c2d12 100%)",
    headerAccentBlob: "#f97316",
    navActive: "#f97316",
    navActiveBg: "rgba(249,115,22,0.08)",
    buttonBg: "#f97316",
    buttonHover: "#ea580c",
    searchRing: "#fdba74",
    accordionFrom: "#f97316",
    accordionTo: "#f59e0b",
    cardHighlight: "#fff7ed",
    filterActive: "#f97316",
    filterActiveBg: "#fff7ed",
    filterActiveBorder: "#fdba74",
    loaderColor: "#f97316",
    modeIcon: "gift",
  },
  discover: {
    id: "discover",
    label: "Discover",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    primaryLight: "#c7d2fe",
    primaryBg: "#faf9ff",
    primaryBgAccent: "#eef2ff",
    pageBg: "#EBEBFF",
    accent: "#0d9488",
    headerGradient: "linear-gradient(135deg, #0c1222 0%, #1e1b4b 50%, #0f2035 100%)",
    headerAccentBlob: "#6366f1",
    navActive: "#6366f1",
    navActiveBg: "rgba(99,102,241,0.08)",
    buttonBg: "#6366f1",
    buttonHover: "#4f46e5",
    searchRing: "#a5b4fc",
    accordionFrom: "#6366f1",
    accordionTo: "#0d9488",
    cardHighlight: "#eef2ff",
    filterActive: "#6366f1",
    filterActiveBg: "#eef2ff",
    filterActiveBorder: "#a5b4fc",
    loaderColor: "#6366f1",
    modeIcon: "compass",
  },
  friends: {
    id: "friends",
    label: "Friends",
    primary: "#22c55e",
    primaryDark: "#16a34a",
    primaryLight: "#bbf7d0",
    primaryBg: "#f7fdf9",
    primaryBgAccent: "#f0fdf4",
    pageBg: "#F0FFF0",
    accent: "#059669",
    headerGradient: "linear-gradient(135deg, #052e16 0%, #14532d 50%, #022c22 100%)",
    headerAccentBlob: "#22c55e",
    navActive: "#22c55e",
    navActiveBg: "rgba(34,197,94,0.08)",
    buttonBg: "#22c55e",
    buttonHover: "#16a34a",
    searchRing: "#86efac",
    accordionFrom: "#22c55e",
    accordionTo: "#059669",
    cardHighlight: "#f0fdf4",
    filterActive: "#22c55e",
    filterActiveBg: "#f0fdf4",
    filterActiveBorder: "#86efac",
    loaderColor: "#22c55e",
    modeIcon: "users",
  },
};

// Feature flags
export const FRIENDS_MODE_ENABLED = false;
export const BESTTIME_HEATMAP_ENABLED = false;
export const SQUARE_POS_ENABLED = false;
export const POSTHOG_ENABLED = process.env.REACT_APP_POSTHOG_ENABLED === "true";

const MODE_STORAGE_KEY = "app_mode";
const MODE_OVERRIDE_KEY = "app_mode_override";
const LAST_AUTO_SWITCH_KEY = "app_last_auto_switch";

function computeDefaultMode(birthday) {
  if (!birthday) return "discover";
  const now = new Date();
  const bday = new Date(birthday);
  // Calculate next birthday
  let nextBday = new Date(now.getFullYear(), bday.getMonth(), bday.getDate());
  if (nextBday < now) {
    // Check if we're still in birthday month (post-birthday but same month)
    const endOfBirthdayMonth = new Date(now.getFullYear(), bday.getMonth() + 1, 0);
    if (now <= endOfBirthdayMonth) {
      return "birthday";
    }
    nextBday = new Date(now.getFullYear() + 1, bday.getMonth(), bday.getDate());
  }
  const daysUntil = Math.ceil((nextBday - now) / (1000 * 60 * 60 * 24));
  return daysUntil <= 30 ? "birthday" : "discover";
}

export const ThemeProvider = ({ children, userBirthday }) => {
  const [mode, setModeState] = useState(() => {
    const saved = localStorage.getItem(MODE_STORAGE_KEY);
    return saved && themes[saved] ? saved : "discover";
  });
  const [isManualOverride, setIsManualOverride] = useState(() => {
    return localStorage.getItem(MODE_OVERRIDE_KEY) === "true";
  });

  // Auto-switch logic based on birthday proximity
  useEffect(() => {
    if (!userBirthday) return;
    const autoMode = computeDefaultMode(userBirthday);
    const lastAutoSwitch = localStorage.getItem(LAST_AUTO_SWITCH_KEY);

    // If user has manually overridden, respect that until the next auto-switch trigger changes
    if (isManualOverride && lastAutoSwitch === autoMode) return;

    // Auto-switch trigger changed (e.g. birthday approaching or ending)
    if (lastAutoSwitch !== autoMode) {
      localStorage.setItem(LAST_AUTO_SWITCH_KEY, autoMode);
      setIsManualOverride(false);
      localStorage.setItem(MODE_OVERRIDE_KEY, "false");
      setModeState(autoMode);
      localStorage.setItem(MODE_STORAGE_KEY, autoMode);

      if (autoMode === "birthday") {
        const bday = new Date(userBirthday);
        const now = new Date();
        let nextBday = new Date(now.getFullYear(), bday.getMonth(), bday.getDate());
        if (nextBday < now) nextBday = new Date(now.getFullYear() + 1, bday.getMonth(), bday.getDate());
        const daysUntil = Math.ceil((nextBday - now) / (1000 * 60 * 60 * 24));
        const message = daysUntil <= 30
          ? `Birthday Mode is on! Your birthday is in ${daysUntil} days!`
          : "Birthday Mode is on! Happy birthday month!";
        setTimeout(() => {
          toast(message, { icon: "🎂", duration: 5000 });
        }, 1500);
      }
    }
  }, [userBirthday, isManualOverride]);

  const setMode = useCallback((newMode) => {
    if (!themes[newMode]) return;
    if (newMode === "friends" && !FRIENDS_MODE_ENABLED) return;
    setModeState(newMode);
    localStorage.setItem(MODE_STORAGE_KEY, newMode);
    setIsManualOverride(true);
    localStorage.setItem(MODE_OVERRIDE_KEY, "true");
  }, []);

  const theme = themes[mode] || themes.discover;

  // Apply CSS custom properties to document root for global access
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--theme-primary", theme.primary);
    root.style.setProperty("--theme-primary-dark", theme.primaryDark);
    root.style.setProperty("--theme-primary-light", theme.primaryLight);
    root.style.setProperty("--theme-primary-bg", theme.primaryBg);
    root.style.setProperty("--theme-page-bg", theme.pageBg);
    root.style.setProperty("--theme-accent", theme.accent);
    root.style.setProperty("--theme-nav-active", theme.navActive);
    root.style.setProperty("--theme-button-bg", theme.buttonBg);
    root.style.setProperty("--theme-loader", theme.loaderColor);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, theme, themes, isManualOverride, FRIENDS_MODE_ENABLED, BESTTIME_HEATMAP_ENABLED, SQUARE_POS_ENABLED, POSTHOG_ENABLED }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
